import { REPORT_SHEET_NAMES } from "../workbook/tableNames";

export type CurrencyPlacement = "prefix" | "suffix";

export interface Currency {
  id: string;
  label: string;
  symbol: string;
  /** The number-format section Excel writes for this currency, spacing included. */
  token: string;
  /** Which side of the number the symbol sits on; in front unless set. */
  placement?: CurrencyPlacement;
}

export const CURRENCIES: readonly Currency[] = [
  { id: "USD", label: "$ English (United States)", symbol: "$", token: "[$$-409]" },
  { id: "GBP", label: "£ English (United Kingdom)", symbol: "£", token: "[$£-809]" },
  { id: "EUR", label: "€ Euro (€123)", symbol: "€", token: "[$€-x-euro2]" },
  // Not offered, since DPR wants every symbol in front; kept as the example of a suffix currency
  // { id: "EUR_SUFFIX", label: "€ Euro (123 €)", symbol: "€", token: "[$€-x-euro1]", placement: "suffix" },
  { id: "SEK", label: "kr Krona (kr123)", symbol: "kr", token: "[$kr-41D]" },
  { id: "CNY", label: "¥ Chinese (PRC)", symbol: "¥", token: "[$¥-804]" },
  { id: "KRW", label: "₩ Korean Won", symbol: "₩", token: "[$₩-412]" },
  { id: "SGD", label: "SGD Singapore", symbol: "SGD", token: "[$SGD] " },
  { id: "CHF", label: "CHF Swiss Franc", symbol: "CHF", token: "[$CHF-100C] " },
  { id: "NOK", label: "NOK Norwegian Krone", symbol: "NOK", token: "[$NOK] " },
];

export type CurrencyScope = "workbook" | "activeSheet";

export interface CurrencyChangeStats {
  sheetsScanned: number;
  sheetsSkipped: number;
  cellsChanged: number;
  /** The one sheet scanned, when the scope was the active sheet. */
  sheetName?: string;
}

const BARE_SYMBOL_LIST = ["$", "£", "€", "¥", "₩"];
const BARE_SYMBOLS = new Set(BARE_SYMBOL_LIST);
const QUOTED_CURRENCIES = new Set([...BARE_SYMBOL_LIST, "kr", "SGD", "CHF", "NOK", "US$", "S$"]);
/** `[$£-809]`, `[$SGD]`: a locale section with a symbol. `[$-409]` carries only a locale. */
const CURRENCY_SECTION = /^\[\$[^-\]]+(-[^\]]*)?\]$/;

const DIGIT_PLACEHOLDERS = new Set(["0", "#", "?"]);

/**
 * Replaces every currency symbol in an Excel number format with `token`, leaving the rest of the
 * format as it was, and puts it on the `placement` side of the number. For a prefix, a symbol
 * already in front is replaced where it stands, and one after the number moves in front of it,
 * ahead of any `*` padding so Accounting columns stay aligned. For a suffix, the symbol follows the
 * number's last digit, ahead of any closing padding. A format with no currency symbol comes back
 * unchanged.
 */
export const swapCurrency = (format: string, token: string, placement: CurrencyPlacement = "prefix"): string => {
  let result = "";
  let changed = false;
  // Per `;`-separated section: where the number starts and ends in `result`, and what it still owes
  let numberStart: number | null = null;
  let numberEnd: number | null = null;
  let seenDigit = false;
  let placedInSection = false;
  let owesSymbol = false;

  const dropSpaceBefore = () => {
    if (result.endsWith("\\ ")) result = result.slice(0, -2);
    else if (result.endsWith(" ")) result = result.slice(0, -1);
  };

  /** Handles the currency symbol ending just before `end`; returns where scanning resumes. */
  const onCurrency = (end: number): number => {
    changed = true;
    if (placement === "suffix") {
      // Placed after the number when the section closes; take the gap to the number with it
      owesSymbol = true;
      if (seenDigit) {
        dropSpaceBefore();
        return end;
      }
      return format[end] === " " ? end + 1 : end;
    }
    if (!seenDigit) {
      // Codes like `[$SGD] ` bring their own space; don't double one the format already has
      result += token.endsWith(" ") && format[end] === " " ? token.trimEnd() : token;
      placedInSection = true;
      return end;
    }
    // A suffix: drop it with the space that separated it from the number, and place it in front later
    dropSpaceBefore();
    owesSymbol = true;
    return end;
  };

  const closeSection = () => {
    if (owesSymbol && !placedInSection) {
      if (placement === "suffix") {
        const at = numberEnd ?? result.length;
        result = `${result.slice(0, at)} ${token.trim()}${result.slice(at)}`;
      } else {
        const at = numberStart ?? result.length;
        result = result.slice(0, at) + token + result.slice(at);
      }
    }
    numberStart = null;
    numberEnd = null;
    seenDigit = false;
    placedInSection = false;
    owesSymbol = false;
  };

  let i = 0;
  while (i < format.length) {
    const ch = format[i];
    if (ch === '"') {
      const close = format.indexOf('"', i + 1);
      const end = close === -1 ? format.length : close + 1;
      const literal = format.slice(i + 1, close === -1 ? format.length : close);
      if (QUOTED_CURRENCIES.has(literal)) {
        i = onCurrency(end);
      } else {
        result += format.slice(i, end);
        i = end;
      }
    } else if (ch === "[") {
      const close = format.indexOf("]", i);
      const end = close === -1 ? format.length : close + 1;
      const section = format.slice(i, end);
      if (CURRENCY_SECTION.test(section)) {
        i = onCurrency(end);
      } else {
        result += section;
        i = end;
      }
    } else if (ch === "\\" && BARE_SYMBOLS.has(format[i + 1])) {
      i = onCurrency(i + 2);
    } else if (ch === "*") {
      if (!seenDigit && numberStart === null) numberStart = result.length;
      result += format.slice(i, i + 2);
      i += 2;
    } else if (ch === "\\" || ch === "_") {
      result += format.slice(i, i + 2);
      i += 2;
    } else if (BARE_SYMBOLS.has(ch)) {
      i = onCurrency(i + 1);
    } else if (ch === ";") {
      closeSection();
      result += ch;
      i += 1;
    } else {
      if (DIGIT_PLACEHOLDERS.has(ch) && !seenDigit) {
        if (numberStart === null) numberStart = result.length;
        seenDigit = true;
      }
      result += ch;
      if (DIGIT_PLACEHOLDERS.has(ch)) numberEnd = result.length;
      i += 1;
    }
  }
  closeSection();
  return changed ? result : format;
};

const isAddinSheet = (name: string): boolean => name.startsWith("__") || REPORT_SHEET_NAMES.has(name);

const CELLS_PER_LOAD = 50_000;
const WRITES_PER_SYNC = 5_000;

/** Rewrites the currency-formatted cells of one sheet's used range and returns how many changed. */
const swapSheetCurrency = async (
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
  currency: Currency
): Promise<number> => {
  const usedRange = sheet.getUsedRangeOrNullObject();
  usedRange.load("isNullObject,rowIndex,columnIndex,rowCount,columnCount");
  await context.sync();
  if (usedRange.isNullObject) return 0;

  const { rowIndex, columnIndex, rowCount, columnCount } = usedRange;
  const tileColumns = Math.min(columnCount, CELLS_PER_LOAD);
  const tileRows = Math.max(1, Math.floor(CELLS_PER_LOAD / tileColumns));
  let cellsChanged = 0;
  let pendingWrites = 0;

  for (let r0 = 0; r0 < rowCount; r0 += tileRows) {
    const rows = Math.min(tileRows, rowCount - r0);
    for (let c0 = 0; c0 < columnCount; c0 += tileColumns) {
      const columns = Math.min(tileColumns, columnCount - c0);
      const tile = sheet.getRangeByIndexes(rowIndex + r0, columnIndex + c0, rows, columns);
      tile.load("numberFormat");
      await context.sync();

      const formats = tile.numberFormat as string[][];
      for (let r = 0; r < rows; r++) {
        let runStart = -1;
        let runFormat = "";
        for (let c = 0; c <= columns; c++) {
          const original = c < columns ? String(formats[r]?.[c] ?? "") : "";
          const swapped = c < columns ? swapCurrency(original, currency.token, currency.placement) : original;
          const isChange = c < columns && swapped !== original;
          if (runStart !== -1 && (!isChange || swapped !== runFormat)) {
            const width = c - runStart;
            sheet.getRangeByIndexes(rowIndex + r0 + r, columnIndex + c0 + runStart, 1, width).numberFormat = [
              Array(width).fill(runFormat),
            ];
            cellsChanged += width;
            pendingWrites += 1;
            runStart = -1;
          }
          if (isChange && runStart === -1) {
            runStart = c;
            runFormat = swapped;
          }
        }
        if (pendingWrites >= WRITES_PER_SYNC) {
          await context.sync();
          pendingWrites = 0;
        }
      }
    }
  }
  await context.sync();
  return cellsChanged;
};

/**
 * Swaps the currency symbol in every currency-formatted cell, on every sheet or only the active one.
 * Only number formats change, never values. The add-in's own hidden sheets are left alone, and a
 * protected sheet, or one that fails partway through, is counted as skipped rather than stopping the
 * rest.
 */
export const changeCurrency = async (currencyId: string, scope: CurrencyScope): Promise<CurrencyChangeStats> => {
  const currency = CURRENCIES.find((c) => c.id === currencyId);
  if (!currency) throw new Error(`Unknown currency: ${currencyId}`);

  return await Excel.run(async (context) => {
    let sheets: Excel.Worksheet[];
    if (scope === "activeSheet") {
      const active = context.workbook.worksheets.getActiveWorksheet();
      active.load("name");
      await context.sync();
      sheets = [active];
    } else {
      const worksheets = context.workbook.worksheets;
      worksheets.load("items/name");
      await context.sync();
      sheets = worksheets.items;
    }

    const targets = sheets.filter((sheet) => !isAddinSheet(sheet.name));
    targets.forEach((sheet) => sheet.protection.load("protected"));
    await context.sync();

    const stats: CurrencyChangeStats = { sheetsScanned: 0, sheetsSkipped: 0, cellsChanged: 0 };
    if (scope === "activeSheet") stats.sheetName = sheets[0].name;
    for (const sheet of targets) {
      if (sheet.protection.protected) {
        stats.sheetsSkipped += 1;
        continue;
      }
      try {
        stats.cellsChanged += await swapSheetCurrency(context, sheet, currency);
        stats.sheetsScanned += 1;
      } catch {
        stats.sheetsSkipped += 1;
      }
    }
    return stats;
  });
};
